import { EnemyData, EnemyIntent, EnemyStance, PlayerState, CombatResult } from '../types';
import { FEATURES } from '../features';
import { applyStatus } from '../combat/StatusSystem';

export type StanceArchetype = 'bruiser' | 'trickster' | 'sentinel';

const STANCE_CYCLES: Record<StanceArchetype, EnemyStance[]> = {
  bruiser: ['aggro', 'guard', 'aggro', 'cast'],
  trickster: ['cast', 'aggro', 'guard', 'aggro'],
  sentinel: ['guard', 'cast', 'aggro', 'guard'],
};

export function getCycleForArchetype(archetype: StanceArchetype): EnemyStance[] {
  return [...STANCE_CYCLES[archetype]];
}

export function ensureEnemyStance(enemy: EnemyData): void {
  if (!FEATURES.enemyStances || enemy.isAdd) return;
  enemy.stanceCycle = enemy.stanceCycle ?? ['aggro', 'guard', 'aggro', 'cast'];
  enemy.stanceIndex = enemy.stanceIndex ?? 0;
  enemy.stance = enemy.stance ?? enemy.stanceCycle[enemy.stanceIndex % enemy.stanceCycle.length] ?? 'aggro';
}

export function advanceEnemyStance(enemy: EnemyData): void {
  if (!FEATURES.enemyStances || enemy.isAdd) return;
  ensureEnemyStance(enemy);
  if (!enemy.stanceCycle || enemy.stanceCycle.length === 0) return;
  enemy.stanceIndex = ((enemy.stanceIndex ?? 0) + 1) % enemy.stanceCycle.length;
  enemy.stance = enemy.stanceCycle[enemy.stanceIndex];
}

export function applyStanceStartTurn(enemy: EnemyData): string[] {
  if (!FEATURES.enemyStances || enemy.isAdd) return [];
  ensureEnemyStance(enemy);
  const logs: string[] = [];
  if (enemy.stance === 'guard') {
    enemy.block += 4;
    logs.push(`${enemy.name} braces in GUARD (+4 block)`);
  }
  return logs;
}

export function stanceIntentValue(enemy: EnemyData, baseValue: number): number {
  if (!FEATURES.enemyStances || enemy.isAdd) return baseValue;
  ensureEnemyStance(enemy);
  if (enemy.stance === 'aggro') {
    return Math.floor(baseValue * 1.2);
  }
  return baseValue;
}

export function getIncomingDamageMultiplier(enemy: EnemyData): number {
  if (!FEATURES.enemyStances || enemy.isAdd) return 1;
  ensureEnemyStance(enemy);
  if (enemy.stance === 'aggro') return 1.1;
  if (enemy.stance === 'guard') return 0.75;
  return 1;
}

export function checkGuardBreak(enemy: EnemyData, attackCardsCommitted: number, positiveAttackTotal: number): boolean {
  if (!FEATURES.enemyStances || enemy.isAdd) return false;
  ensureEnemyStance(enemy);
  if (enemy.stance !== 'guard') return false;
  const broken = attackCardsCommitted >= 2 || positiveAttackTotal >= 8;
  if (broken) {
    applyStatus(enemy, 'brittle', 1, 1);
  }
  return broken;
}

export function resolveCastPayload(
  enemy: EnemyData,
  player: PlayerState,
  result: Pick<CombatResult, 'combatLog'>,
): void {
  if (!FEATURES.enemyStances || enemy.isAdd) return;
  ensureEnemyStance(enemy);
  if (enemy.stance !== 'cast') return;

  const lowerName = enemy.name.toLowerCase();
  if (lowerName.includes('slime') || lowerName.includes('fungus') || lowerName.includes('crystal')) {
    applyStatus(player, 'frail', 1, 2);
    result.combatLog?.push(`${enemy.name} casts: you are FRAIL`);
    return;
  }

  if (lowerName.includes('gremlin') || lowerName.includes('wraith') || lowerName.includes('shade')) {
    applyStatus(player, 'jammed', 1, 1);
    result.combatLog?.push(`${enemy.name} casts: your first ability is JAMMED`);
    return;
  }

  applyStatus(player, 'exposed', 1, 2);
  result.combatLog?.push(`${enemy.name} casts: you are EXPOSED`);
}

export function forceBossStanceForPhase(enemy: EnemyData): void {
  if (!FEATURES.enemyStances || !enemy.boss) return;
  const phase = enemy.phase ?? 1;
  if (phase === 1) {
    enemy.stance = 'guard';
  } else if (phase === 2) {
    enemy.stance = 'cast';
  } else {
    enemy.stance = 'aggro';
  }
}

export function withCastIntentIfNeeded(enemy: EnemyData, baseIntent: EnemyIntent): EnemyIntent {
  if (!FEATURES.enemyStances || enemy.isAdd) return baseIntent;
  ensureEnemyStance(enemy);
  if (enemy.stance !== 'cast') return baseIntent;
  return {
    type: 'special',
    value: 0,
    description: 'Cast pattern',
  };
}
